/**
 * Когда показывать перекличку героев (ISSUE-122, ISSUE-145, MADR 0030, MADR 0037). Новый герой важнее первого боя сессии, тот
 * важнее босса: это полная перекличка на 3 с. Перед обычной волной — плашка «Волна N» на 1 с с позами героев, получивших
 * уровень с прошлого боя; она кончается вместе с паузой волны. На волне 1 показа нет. Решение принимается только пока
 * зомби не вышли: фаза `intro` волны.
 */
import type { BattleSnapshot, HeroId } from "@newfungame/contracts";
import { bossIntro, heroIntro, sessionIntro, WAVE_PLATE_TICKS, waveIntro, type IntroPlan } from "../field/intro";
import { createStore } from "./state/create-store";

/** Начало ключа «новый герой уже показан»: на устройство, откат на ту же волну показ не повторяет. */
export const INTRO_HERO_PREFIX = "nfg:introHero:";

export interface HeroIntroPlate {
  heroId: HeroId;
  /** Над головой героя, px экрана. */
  x: number;
  y: number;
  /** Секунды от начала показа. */
  from: number;
  to: number;
  /** Уровень героя: подпись плашки «Волна N» вместо навыка. */
  level?: number;
}

export interface HeroIntroView {
  id: number;
  kind: IntroPlan["kind"];
  plates: HeroIntroPlate[];
  /** Плашка «Волна N»: номер и длина, секунды. */
  wave?: { number: number; seconds: number };
  /** Вспышка кнопки навыка, секунды от начала показа. */
  pulses: Partial<Record<HeroId, number>>;
}

export const heroIntroStore = createStore<HeroIntroView | null>(null);

export interface HeroIntroDeps {
  field: {
    setIntro(plan: IntroPlan | null): void;
    /** Над головой героя, px экрана; `null` — герой ещё не нарисован. */
    heroAnchor(heroId: HeroId): { x: number; y: number } | null;
  };
  /** Кадры героев и эффектов загружены: на воксельных заглушках показа нет. */
  ready(): boolean;
  speed(): number;
  reducedMotion: boolean;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  onBeat?(heroId: HeroId): void;
  store?: typeof heroIntroStore;
}

export interface HeroIntro {
  onTick(s: BattleSnapshot): void;
  /** Тап в любое место: показ снимается сразу. */
  stop(): void;
}

const TICKS_PER_SECOND = 30;
/** Выше этой скорости (x5, x10 — инструменты админа) только новый герой: остальное не успеть прочитать. */
const MAX_SHOW_SPEED = 2;

export function createHeroIntro(deps: HeroIntroDeps): HeroIntro {
  const store = deps.store ?? heroIntroStore;
  let plan: IntroPlan | null = null;
  let played = 0;
  let id = 0;
  let sessionPending = true;
  let decided = false;
  let newHero: HeroId | null = null;
  let lastWave = 0;
  let lastTick = -1;
  /** Уровни героев на прошлом решении: с ними сравнивает плашка «Волна N». */
  let levels = new Map<HeroId, number>();

  const seen = (heroId: HeroId): boolean => {
    try {
      return deps.storage?.getItem(INTRO_HERO_PREFIX + heroId) === "1";
    } catch {
      return false;
    }
  };
  const remember = (heroId: HeroId): void => {
    try {
      deps.storage?.setItem(INTRO_HERO_PREFIX + heroId, "1");
    } catch {
      // без localStorage героя покажем ещё раз, если волна повторится
    }
  };

  const stop = (): void => {
    if (!plan) return;
    plan = null;
    deps.field.setIntro(null);
    store.set(null);
  };

  /** Какой показ нужен этой волне. */
  const choose = (s: BattleSnapshot): IntroPlan | null => {
    const heroes = s.heroes.map((h) => h.heroId);
    const before = levels;
    levels = new Map(s.heroes.map((h) => [h.heroId, h.level]));
    if (newHero && !seen(newHero)) {
      remember(newHero);
      return heroIntro(newHero, s.wave.tick);
    }
    const session = sessionPending;
    sessionPending = false;
    const speed = deps.speed();
    if (speed > MAX_SHOW_SPEED || heroes.length === 0) return null;
    if (session) return sessionIntro(heroes, s.wave.tick);
    if (s.wave.isBoss) return bossIntro(heroes, s.wave.tick);
    // 1 с настоящего времени: на x2 плашка вдвое длиннее в тиках, а пауза волны её всё равно обрежет
    const levelled = s.heroes.filter((h) => h.level > (before.get(h.heroId) ?? h.level)).map((h) => h.heroId);
    return waveIntro(levelled, s.wave.tick, Math.round(WAVE_PLATE_TICKS * Math.max(1, speed)));
  };

  const start = (next: IntroPlan): void => {
    const speed = Math.max(1, deps.speed());
    const seconds = (t: number) => t / TICKS_PER_SECOND / speed;
    const level = (heroId: HeroId) => (next.kind === "wave" ? levels.get(heroId) : undefined);
    const plates: HeroIntroPlate[] = [];
    const pulses: HeroIntroView["pulses"] = {};
    for (const b of next.beats) {
      pulses[b.heroId] = seconds(b.start);
      const at = b.plate && deps.field.heroAnchor(b.heroId);
      if (b.plate && at)
        plates.push({
          heroId: b.heroId,
          ...at,
          from: seconds(b.plate.from),
          to: seconds(b.plate.to),
          level: level(b.heroId),
        });
    }
    plan = next;
    played = 0;
    deps.field.setIntro(next);
    const wave = next.kind === "wave" ? { number: lastWave, seconds: seconds(next.length) } : undefined;
    store.set({ id: ++id, kind: next.kind, plates, pulses, ...(wave ? { wave } : {}) });
  };

  const play = (s: BattleSnapshot, current: IntroPlan): void => {
    const age = s.wave.tick - current.startTick;
    // плашка «Волна N» не заходит в бой: зомби вышли — снимаем
    const over =
      current.kind === "wave" ? s.wave.phase !== "intro" : s.wave.phase === "cleared" || s.wave.phase === "lost";
    if (age >= current.length || over) {
      stop();
      return;
    }
    while (played < current.beats.length && current.beats[played]!.start <= age) {
      deps.onBeat?.(current.beats[played]!.heroId);
      played++;
    }
  };

  return {
    stop,
    onTick(s) {
      if (s.wave.number !== lastWave || s.wave.tick < lastTick) {
        stop();
        decided = false;
        newHero = null;
      }
      lastWave = s.wave.number;
      lastTick = s.wave.tick;
      for (const e of s.events) if (e.type === "heroUnlocked") newHero = e.heroId;
      if (plan) {
        play(s, plan);
        return;
      }
      // волна 1 начинается почти без паузы: показ остаётся на волну 2
      if (decided || deps.reducedMotion || s.wave.phase !== "intro" || s.wave.number < 2 || s.wave.tick < 1) return;
      if (!deps.ready() || s.heroes.some((h) => !deps.field.heroAnchor(h.heroId))) return;
      decided = true;
      const next = choose(s);
      if (next) start(next);
    },
  };
}
